<template>
	<datatable
		:headers
		:value="computedItems"
		container-class="min-h-[500px]"
		clickable
		expandable
		selectable
		:status-key="stage"
		:filter-options="filterOptions"
		:global-filter-fields="filterFields"
		:loading
		@row-click="(item) => emit('clickedItem', item)"
		@update:items-selected="(selections) => (selected = selections)">
		<template #filter-picking_reference="{ model, callback }">
			<app-dropdown
				v-if="model"
				v-model="model.value"
				label-key="value"
				size="small"
				:options="pickRefOptions"
				@update:model-value="callback()">
				Pick Ref
			</app-dropdown>
		</template>

		<template #filter-delivery_method="{ model, callback }">
			<app-dropdown
				v-if="model"
				v-model="model.value"
				label-key="value"
				size="small"
				:options="deliveryMethods"
				@update:model-value="callback()">
				Delivery Method
			</app-dropdown>
		</template>

		<template #filter-depot="{ model, callback }">
			<app-dropdown
				v-if="model"
				v-model="model.value"
				label-key="value"
				size="small"
				:options="depotFilterOptions"
				@update:model-value="callback()">
				Depot
			</app-dropdown>
		</template>

		<template #filter-journey_number="{ model, callback }">
			<app-dropdown
				v-if="model"
				v-model="model.value"
				label-key="value"
				size="small"
				:options="getJourneyNumberOptions(items as CollectionItem[])"
				clearable
				@update:model-value="callback()">
				Journey No
			</app-dropdown>
		</template>

		<template #row-collection_status="{ row }">
			<div class="flex items-center mt-2 mb-2">
				<app-badge class="gap-2" size="small" :theme="getStatusClass(row.status)">
					{{ row.status }}
				</app-badge>
			</div>
		</template>

		<template #row-quantity_required="{ row }">
			{{ statusParam(row, 'quantity_required', stage) ?? '-' }}
		</template>

		<template #row-quantity_confirmed="{ row }">
			<div
				v-if="
					row.type != 'Kit' && row.stock_check_status && row.stock_check_status.length > 0
				"
				style="max-width: 60px">
				<app-input
					v-model="row.stock_check_status[0].quantity_confirmed"
					type="number"
					size="small" />
			</div>
			<div v-else-if="row.type != 'Kit'" style="max-width: 60px">
				{{ statusParam(row, 'quantity_confirmed', stage) ?? 0 }}
			</div>
			<span v-else>-</span>
		</template>

		<template #row-quantity_loaded="{ row }">
			{{ statusParam(row, 'quantity_loaded', 'pick') }}
		</template>

		<template #row-delivery_method="{ row }">
			<div class="font-semibold">
				{{ statusParam(row, 'delivery_method', 'pick') }}
			</div>
			<div>
				{{ statusParam(row, 'delivery_method_name', 'pick') }}
			</div>
		</template>

		<template #row-pick_status="{ row }">
			{{ statusParam(row, 'pick_number', 'pick') }}
		</template>

		<template #row-assigned="{ row }">
			<div
				v-if="row.stock_check_status && row.stock_check_status.length > 0"
				class="mt-2 mb-2">
				<operators-dropdown
					v-model="row.stock_check_status[0].assigned_to"
					boundary-element=".p-datatable"
					@update:model-value="(value: Row) => emit('update', value)" />
			</div>
			<div v-else-if="row.pick_status && row.pick_status.length > 0" class="mt-2 mb-2">
				<operators-dropdown
					v-model="row.pick_status[0].assigned_to"
					boundary-element=".p-datatable"
					@update:model-value="(value: Row) => emit('update', value)" />
			</div>
		</template>

		<template #row-assigned_load="{ row }">
			<div v-if="row.pick_status && row.pick_status.length > 0" class="mt-2 mb-2">
				<operators-dropdown
					v-model="row.pick_status[0].load_assigned_to"
					boundary-element=".p-datatable"
					@update:model-value="(value: Row) => emit('update', value)" />
			</div>
		</template>

		<template #row-collection_assigned_to="{ row }">
			<operators-dropdown
				v-model="row.assigned_to"
				boundary-element=".p-datatable"
				@update:model-value="emit('update', row)" />
		</template>

		<template #row-weight="{ row }">
			{{ (row.weight * 1000).toFixed(1) }}
		</template>

		<template #row-notes="{ row }">
			<div v-if="row.stock_check_status && row.stock_check_status.length > 0">
				<notes-button
					v-if="row.stock_check_status && row.stock_check_status[0]"
					:status="row.stock_check_status[0]"
					@click="editingContract = row" />
			</div>
			<div v-else-if="row.pick_status && row.pick_status.length > 0">
				<notes-button
					v-if="row.pick_status && row.pick_status[0]"
					:status="row.pick_status[0]"
					@click="editingContract = row" />
			</div>
		</template>

		<template #row-collection_notes="{ row }">
			<notes-button
				:status="{ notes: row.notes, notes_status: row.notes_status }"
				@click="editingCollection = row" />
		</template>
	</datatable>

	<edit-contract-item-note
		:item="editingContract"
		:area="stage"
		:resolve-on-save="stage == 'stock-check' ? undefined : true"
		@updated="emit('updatedItems')"
		@closed="editingContract = undefined" />

	<edit-collection-item-note
		:item="editingCollection"
		:resolve-on-save="true"
		@updated="emit('updatedItems')"
		@closed="editingCollection = undefined" />
</template>

<script lang="ts" setup>
	import { computed, ref, watch } from 'vue'
	import { getStatusClass } from '@/composables/datatable'
	import OperatorsDropdown from '@/components/partials/dropdowns/OperatorsDropdown.vue'
	import type { ContractItem } from '@/data/schema/contract-item'
	import useContracts, { statusParam } from '@/composables/contracts'
	import NotesButton from '@/components/partials/cat2/NotesButton.vue'
	import EditContractItemNote from '@/components/partials/cat2/EditContractItemNote.vue'
	import type { WorkspaceArea } from '@/data/types/area'
	import { JourneyItem } from '@/data/schema/journey-item'
	import type { CollectionItem } from '@/data/schema/collection-item'
	import EditCollectionItemNote from '@/components/partials/cat2/EditCollectionItemNote.vue'
	import type { Header, Row } from '@/data/types/datatable'
	import useCollections from '@/composables/collections'
	import { FilterMatchMode } from '@primevue/core/api'
	import { contractItemStatusMap } from '@/utils/statusMaps'

	const selected = ref<Array<ContractItem | JourneyItem>>([])
	const editingContract = ref<ContractItem | undefined>(undefined)
	const editingCollection = ref<CollectionItem | undefined>(undefined)
	const { getJourneyNumberOptions } = useCollections()

	const props = defineProps({
		items: {
			type: Array<ContractItem | CollectionItem | JourneyItem>,
			default: []
		},
		headers: {
			type: Array<Header>,
			default: []
		},
		loading: Boolean,
		stage: {
			type: String as () => WorkspaceArea,
			default: 'stock-check'
		}
	})

	// Filtering
	const { getPickingRefOptions, getDeliveryMethodOptions, getDepotOptions } = useContracts()
	const pickRefOptions = ref([])
	const deliveryMethods = ref([])
	const depotFilterOptions = ref([])

	const filterOptions = ref({
		journey_number: { value: null, matchMode: FilterMatchMode.EQUALS },
		picking_reference: { value: null, matchMode: FilterMatchMode.EQUALS },
		delivery_method: { value: null, matchMode: FilterMatchMode.EQUALS },
		depot: { value: null, matchMode: FilterMatchMode.EQUALS }
	})

	const filterFields = computed(() => {
		let fields = []

		if (
			props.stage == 'collections' &&
			getJourneyNumberOptions(props.items as CollectionItem[]).length > 1
		) {
			fields.push('journey_number')
		}

		if (pickRefOptions.value.length > 1) {
			fields.push('picking_reference')
		}

		if (deliveryMethods.value.length > 1) {
			fields.push('delivery_method')
		}

		if (depotFilterOptions.value.length > 1) {
			fields.push('depot')
		}

		return fields
	})

	// Move data to top layer for client side filtering
	const computedItems = computed(() => {
		const status = contractItemStatusMap[props.stage]

		return props.items.map((item) => ({
			...item,
			delivery_method: item[status]?.[0]?.delivery_method ?? null
		}))
	})

	// Events
	const emit = defineEmits(['select', 'update', 'updatedItems', 'clickedItem'])

	// Watchers
	watch(
		selected,
		() => {
			emit('select', selected.value)
		},
		{ deep: true }
	)

	watch(
		() => props.loading,
		(newValue) => {
			if (!newValue) {
				selected.value = []

				if (props.stage != 'journey' && props.stage != 'collections') {
					pickRefOptions.value = getPickingRefOptions(props.items as ContractItem[])
					deliveryMethods.value = getDeliveryMethodOptions(
						props.items as ContractItem[],
						props.stage
					)
				}

				depotFilterOptions.value = getDepotOptions(
					props.items,
					contractItemStatusMap[props.stage]
				)
			}
		}
	)
</script>
